import {
    createContext,
    useCallback,
    useContext,
    useId,
    useMemo,
    useState,
    type ReactElement,
    type ReactNode,
} from 'react';

import { Button } from './Button.js';
import styles from './Toast.module.css';

export interface ToastRequest {
    message: string;
    actionLabel?: string | undefined;
    onAction?: (() => void) | undefined;
}

interface ToastItem extends ToastRequest {
    id: string;
}

interface ToastContextValue {
    showToast: (request: ToastRequest) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
const TOAST_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
    const [items, setItems] = useState<ToastItem[]>([]);
    const regionId = useId();

    const showToast = useCallback((request: ToastRequest): void => {
        const id = crypto.randomUUID();
        setItems((current) => [...current, { ...request, id }]);
        window.setTimeout(() => {
            setItems((current) => current.filter((item) => item.id !== id));
        }, TOAST_MS);
    }, []);

    const value = useMemo(() => ({ showToast }), [showToast]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div
                id={regionId}
                className={styles.region}
                aria-live="polite"
                aria-relevant="additions"
            >
                {items.map((item) => (
                    <div key={item.id} className={styles.toast} role="status">
                        <p>{item.message}</p>
                        {item.actionLabel !== undefined && item.onAction !== undefined ? (
                            <Button
                                variant="ghost"
                                className={styles.action}
                                onClick={() => {
                                    item.onAction?.();
                                    setItems((current) =>
                                        current.filter((entry) => entry.id !== item.id),
                                    );
                                }}
                            >
                                {item.actionLabel}
                            </Button>
                        ) : null}
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

/** Hook for the nearest ToastProvider. Kept next to the provider so callers share one import. */
// eslint-disable-next-line react-refresh/only-export-components -- hook is bound to ToastProvider
export function useToast(): ToastContextValue {
    const context = useContext(ToastContext);
    if (context === null) {
        throw new Error('useToast must be used inside ToastProvider');
    }
    return context;
}
